import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import type { Href } from 'expo-router';
import React, { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Platform, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  HeaderFrame,
  HeaderIconButton,
  HeaderTitleText,
  IconClose,
  IconList,
  IconMap,
} from '@fhs/ui';

import { StoreLocatorViews } from '../types';

import { useStoreLocatorContext } from './store-locator-provider';

type StoreLocatorScreenHeaderProps = {
  navigation: { canGoBack: () => boolean };
};

const orderTypeFocusTargetProps =
  Platform.OS === 'web' ? ({ 'data-fhs-order-type-focus-target': 'true' } as const) : {};

/**
 * Dedicated header component so navigation, focus, and intl hooks stay valid.
 * Web: moves focus to the close control on screen focus so route changes do not leave focus on shell chrome.
 */
export function StoreLocatorScreenHeader({ navigation }: StoreLocatorScreenHeaderProps) {
  const { formatMessage } = useIntl();
  const { top } = useSafeAreaInsets();
  const { closeTo } = useLocalSearchParams<{ closeTo?: string | string[] }>();
  const { currentView, updateStoreLocatorView } = useStoreLocatorContext();
  const closeButtonRef = useRef<View>(null);

  const closeToHref = typeof closeTo === 'string' ? closeTo : closeTo?.[0];

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== 'web') {
        return;
      }
      const id = requestAnimationFrame(() => {
        const el = closeButtonRef.current as unknown as { focus?: () => void } | null;
        el?.focus?.();
      });
      return () => cancelAnimationFrame(id);
    }, [])
  );

  const handleClose = useCallback(() => {
    if (closeToHref) {
      router.dismissTo(closeToHref as Href);
      return;
    }
    if (navigation.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/');
  }, [closeToHref, navigation]);

  return (
    // Keep the navigation header's height stable while the native screen mounts.
    <View style={{ paddingTop: top, backgroundColor: 'white' }}>
      <HeaderFrame shouldApplySafeAreaInset={false}>
        <HeaderFrame.Left>
          <HeaderIconButton
            ref={closeButtonRef}
            {...orderTypeFocusTargetProps}
            accessibilityLabel={formatMessage({ id: 'close' })}
            onPress={handleClose}
          >
            <IconClose />
          </HeaderIconButton>
        </HeaderFrame.Left>
        <HeaderFrame.Middle>
          <HeaderTitleText accessibilityLevel={2} text="Locations" />
        </HeaderFrame.Middle>
        <HeaderFrame.Right>
          <HeaderIconButton
            accessibilityLabel={`Change to ${
              currentView === StoreLocatorViews.LIST_VIEW ? 'map' : 'list'
            } view`}
            onPress={() =>
              updateStoreLocatorView(
                currentView === StoreLocatorViews.LIST_VIEW
                  ? StoreLocatorViews.MAP_VIEW
                  : StoreLocatorViews.LIST_VIEW
              )
            }
          >
            {currentView === StoreLocatorViews.LIST_VIEW ? <IconMap /> : <IconList />}
          </HeaderIconButton>
        </HeaderFrame.Right>
      </HeaderFrame>
    </View>
  );
}
